import type { Shop } from '@skop-app/lib-types'

export type ShareInfo = {
  description?: string
  majorCategories?: Array<{ label: string; value: string }>
  majorProvenances?: Array<{ label: string; value: string }>
  featuredOrder?: number
}

export type ShopForTasks = Partial<Omit<Shop, 'shareInfo'>> & {
  shareInfo: ShareInfo | null
}

export type SelectOption<TValue extends string> = {
  label: string
  value: TValue
}

/**
 * Connector values (single source of truth).
 * Keep these synced with `src/schemas/connector.schema.ts`.
 */
export const CONNECTOR_PLATFORMS = [
  'shopify',
  'prestashop',
  'woocommerce',
  'matrys',
] as const
export type ConnectorPlatform = (typeof CONNECTOR_PLATFORMS)[number]

export const CONNECTOR_UPDATE_FREQUENCIES = [
  'hourly',
  'threeTimesDaily',
  'daily',
  'disabled',
] as const
export type ConnectorUpdateFrequency =
  (typeof CONNECTOR_UPDATE_FREQUENCIES)[number]

export type ConnectorPlatformOption = SelectOption<ConnectorPlatform>
export type ConnectorUpdateFrequencyOption =
  SelectOption<ConnectorUpdateFrequency>

export type ConnectorCategorySelection = {
  selected: boolean
  id: string
  name: string
}

export type ConnectorCustomFieldPair = { key: string; value: string }

/**
 * Persisted connector credentials stored in `Shop.credentials` (GraphQL: `credentials: a.json()`).
 *
 * Notes:
 * - `partner` also exists as a root `Shop.partner` field in the GraphQL schema; keep it optional
 *   here only for backward compatibility with existing stored JSON.
 */
export type ConnectorCredentials = {
  platform?: ConnectorPlatform
  apiKey?: string
  apiSecret?: string
  shopUrl?: string
  updateFrequency?: ConnectorUpdateFrequency
  currency?: string
  taxRate?: number
  removeVAT?: boolean
  categories?: ConnectorCategorySelection[]
  customFields?: Record<string, string>
  partner?: string
}

/**
 * Form payload shape used by the connector settings UI and `/api/shop/categories`.
 */
export type ConnectorParams = {
  platform: ConnectorPlatformOption
  apiKey: string
  apiSecret?: string
  shopUrl: string
  updateFrequency: ConnectorUpdateFrequencyOption
  currency?: { label: string; value: string | number; sign?: string } | null
  taxRate?: number
  removeVAT: boolean
  categories?: ConnectorCategorySelection[]
  customFields?: ConnectorCustomFieldPair[]
}

/**
 * Default/initial values for the connector form.
 * Select fields are optional to support "no selection yet" states.
 */
export type ConnectorFormValues = Omit<
  ConnectorParams,
  'platform' | 'updateFrequency'
> & {
  platform?: ConnectorPlatformOption
  updateFrequency?: ConnectorUpdateFrequencyOption
}

export type UpdateShopInput = Omit<
  Partial<Shop>,
  'credentials' | 'shareImage' | 'logo'
> & {
  id: string
  credentials?: ConnectorCredentials | null
  /** Explicit null clears the existing logo; omission preserves it. */
  logo?: string | null
  /**
   * Explicitly nullable to allow clearing a previously uploaded share image.
   */
  shareImage?: string | null
}
export type CreateShopInput = UpdateShopInput & { userID: string }
